import { injectScript } from '@cognitoforms/utils/utils';

/**
 * Legacy admin page names.
 */
export type AdminPageName = 'publish' | 'build' | 'entries' | 'entry-details'
| 'build-action-bar' | 'build-form-settings' | 'build-payment-settings'
| 'build-field-settings' | 'build-workflow-settings' | 'build-canvas' | 'build-canvas-settings-workflow' | 'build-canvas-settings-payment'
| 'build-save-error-banner' | 'build-edit-notification-dialog' | 'build-edit-email-dialog' | 'build-edit-tracking-pixel-dialog' | 'build-range-field-editor-template';

/**
 * The type of a "legacy" script that has been modified to support re-evaulation
 * after client-side page navigation in order to re-establish state. The script is
 * wrapped with an IIFE that is also assigned to the module exports as an 'exec' function.
 */
export type LegacyScriptModule = {
	exec: () => void;
};

// A dictionary of legacy page script modules that have been imported.
const staticPageScriptModules: Partial<Record<AdminPageName, LegacyScriptModule>> = {};

/**
 * Loads the given script module, either by getting it from the cache and
 * re-evaluating it, or by fetching the script via the given function.
 * @param name The name of the script
 * @param getScriptModule A function to fetch the script
 */
export async function loadScript(name: string, getScriptModule: () => Promise<LegacyScriptModule>) {
	let scriptModule = staticPageScriptModules[name];
	if (!scriptModule) {
		scriptModule = await getScriptModule();
		if (typeof scriptModule.exec !== 'function')
			throw new Error(`Module "${name}" does not define an exec function.`);
		staticPageScriptModules[name] = scriptModule;
	}
	else {
		if (typeof scriptModule.exec !== 'function')
			throw new Error(`Module "${name}" does not define an exec function.`);
		scriptModule.exec();
	}
}

export async function linkStylesheet(href: string) {
	const link = document.createElement('link');
	link.rel = 'stylesheet';
	link.href = href;
	document.head.appendChild(link);
}

export async function unlinkStylesheet(href: string) {
	document.querySelector(`[href="${href}"]`)?.remove();
}

/**
 * Injects a script tag for a page's dynamic page script, given the url for the endpoint.
 * @returns A promise that resolves when the script tag has finished loading.
 */
export function injectDynamicPageScript(name: AdminPageName, url: string) {
	const scriptId = name + '-' + Date.now();
	return injectScript(url, { scriptId });
}

export type LegacyPage = {
	initialized: Promise<unknown>;
	initializeLegacyPage(fromPath?: string, scriptParams?: unknown): Promise<unknown>;
	disposeLegacyPage(): Promise<unknown>;
	trackRecentPages(): void;
};